import React, { useMemo, useState } from "react";
import { Avatar, Button, Skeleton } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import AppShell from "../../components/app-shell/AppShell";
import Sheet from "../../components/app-shell/Sheet";
import FeedComposer from "../../components/member-feed/FeedComposer";
import FeedPostCard from "../../components/member-feed/FeedPostCard";
import { DEFAULT_POST_TYPE } from "../../components/member-feed/postTypes";
import "./member.css";

const SORT_OPTIONS = [
  { label: "Newest", value: "newest" },
  { label: "Most liked", value: "like-count" },
  { label: "Most shared", value: "share-count" },
];

const updatePostInPages = (oldData, postId, updater) => {
  if (!oldData?.pages) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      posts: (page.posts || []).map((post) =>
        String(post._id) === String(postId) ? updater(post) : post
      ),
    })),
  };
};

const Feed = ({ endUser }) => {
  const queryClient = useQueryClient();
  const {
    getCommunityFeed,
    createCommunityPost,
    addCommentToPost,
    editCommunityPost,
    editCommunityComment,
    toggleCommunityPostLike,
    shareCommunityPost,
  } = useUser();

  const [postText, setPostText] = useState("");
  const [postType, setPostType] = useState(DEFAULT_POST_TYPE);
  const [commentByPost, setCommentByPost] = useState({});
  const [sortBy, setSortBy] = useState("newest");
  const [myPostsOnly, setMyPostsOnly] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const feedQueryKey = ["community-feed", "infinite", sortBy, myPostsOnly];

  const {
    data: feedData,
    isLoading: feedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: feedQueryKey,
    queryFn: ({ pageParam = null }) =>
      getCommunityFeed({ cursor: pageParam, limit: 20, sortBy, myPostsOnly }),
    initialPageParam: null,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.hasMore) return undefined;
      return lastPage?.nextCursor;
    },
  });

  const feed = useMemo(() => {
    return (feedData?.pages || []).flatMap((page) => page?.posts || []);
  }, [feedData]);

  const { mutate: createPostAction, isPending: posting } = useMutation({
    mutationFn: createCommunityPost,
    onMutate: async ({ text, postType: creatingType }) => {
      await queryClient.cancelQueries({ queryKey: feedQueryKey });
      const previousFeed = queryClient.getQueryData(feedQueryKey);

      const tempId = `temp-${Date.now()}`;
      const optimisticPost = {
        _id: tempId,
        text,
        postType: creatingType || DEFAULT_POST_TYPE,
        likes: [],
        createdAt: new Date().toISOString(),
        comments: [],
        userId: {
          _id: endUser?._id,
          name: endUser?.name || "You",
          blood: endUser?.blood || "",
          district: endUser?.district || "",
          upuzila: endUser?.upuzila || "",
          profilePhoto: endUser?.profilePhoto || "",
        },
      };

      queryClient.setQueryData(feedQueryKey, (oldData) => {
        if (!oldData?.pages?.length) {
          return {
            pages: [{ posts: [optimisticPost], hasMore: false, nextCursor: null }],
            pageParams: [null],
          };
        }
        const [firstPage, ...restPages] = oldData.pages;
        return {
          ...oldData,
          pages: [
            {
              ...firstPage,
              posts: [optimisticPost, ...(firstPage.posts || [])],
            },
            ...restPages,
          ],
        };
      });

      return { previousFeed, tempId };
    },
    onSuccess: (savedPost, _variables, context) => {
      setPostText("");
      setPostType(DEFAULT_POST_TYPE);
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, context?.tempId, () => savedPost)
      );
      toast.success("Posted");
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(feedQueryKey, context?.previousFeed);
      toast.error(err?.response?.data?.message || "Failed to post");
    },
  });

  const { mutate: commentAction, isPending: commenting } = useMutation({
    mutationFn: addCommentToPost,
    onMutate: async ({ postId, text }) => {
      await queryClient.cancelQueries({ queryKey: feedQueryKey });
      const previousFeed = queryClient.getQueryData(feedQueryKey);

      const optimisticComment = {
        _id: `temp-comment-${Date.now()}`,
        text,
        createdAt: new Date().toISOString(),
        userId: {
          _id: endUser?._id,
          name: endUser?.name || "You",
          blood: endUser?.blood || "",
          district: endUser?.district || "",
          upuzila: endUser?.upuzila || "",
          profilePhoto: endUser?.profilePhoto || "",
        },
      };

      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, postId, (post) => ({
          ...post,
          comments: [...(post.comments || []), optimisticComment],
        }))
      );

      return { previousFeed, postId };
    },
    onSuccess: (updatedPost, variables) => {
      setCommentByPost((prev) => ({ ...prev, [variables.postId]: "" }));
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, variables.postId, () => updatedPost)
      );
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(feedQueryKey, context?.previousFeed);
      toast.error(err?.response?.data?.message || "Failed to comment");
    },
  });

  const { mutate: likeAction, isPending: liking } = useMutation({
    mutationFn: toggleCommunityPostLike,
    onMutate: async ({ postId }) => {
      await queryClient.cancelQueries({ queryKey: feedQueryKey });
      const previousFeed = queryClient.getQueryData(feedQueryKey);
      const me = String(endUser?._id || "");

      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, postId, (post) => {
          const likes = Array.isArray(post.likes) ? [...post.likes] : [];
          const idx = likes.findIndex((id) => String(id) === me);
          if (idx > -1) {
            likes.splice(idx, 1);
          } else if (me) {
            likes.push(me);
          }
          return {
            ...post,
            likes,
          };
        })
      );

      return { previousFeed, postId };
    },
    onSuccess: (updatedPost, variables) => {
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, variables.postId, () => updatedPost)
      );
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(feedQueryKey, context?.previousFeed);
      toast.error(err?.response?.data?.message || "Failed to update like");
    },
  });

  const { mutate: shareAction } = useMutation({
    mutationFn: shareCommunityPost,
    onMutate: async ({ postId }) => {
      await queryClient.cancelQueries({ queryKey: feedQueryKey });
      const previousFeed = queryClient.getQueryData(feedQueryKey);
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, postId, (post) => ({
          ...post,
          shareCount: Number(post?.shareCount || 0) + 1,
        }))
      );
      return { previousFeed };
    },
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, updatedPost?._id, () => updatedPost)
      );
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(feedQueryKey, context?.previousFeed);
    },
  });

  const { mutate: editPostAction, isPending: editingPost } = useMutation({
    mutationFn: editCommunityPost,
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, updatedPost?._id, () => updatedPost)
      );
      toast.success("Post updated");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to edit post");
    },
  });

  const { mutate: editCommentAction, isPending: editingComment } = useMutation({
    mutationFn: editCommunityComment,
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(feedQueryKey, (oldData) =>
        updatePostInPages(oldData, updatedPost?._id, () => updatedPost)
      );
      toast.success("Comment updated");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to edit comment");
    },
  });

  const handleCreatePost = () => {
    const text = String(postText || "").trim();
    if (!text) return toast.error("Write something to post");
    createPostAction({ text, postType });
    setComposerOpen(false);
  };

  const handleAddComment = (postId) => {
    const text = String(commentByPost[postId] || "").trim();
    if (!text) return toast.error("Write a comment first");
    commentAction({ postId, text });
  };

  const handleToggleLike = (postId) => {
    likeAction({ postId });
  };

  const handleSharePost = async (postId) => {
    const shareUrl = `${window.location.origin}/post/${postId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "0709 Bangladesh Community Post",
          text: "Check out this post",
          url: shareUrl,
        });
        shareAction({ postId });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        shareAction({ postId });
        toast.success("Post link copied");
        return;
      }

      toast.info(shareUrl);
    } catch (_error) {
      toast.error("Could not share post");
    }
  };

  return (
    <AppShell>
      <button type="button" className="feed-prompt" onClick={() => setComposerOpen(true)}>
        <Avatar size={40} src={endUser?.profilePhoto || undefined} icon={<UserOutlined />} />
        <span className="feed-prompt__text">What's on your mind?</span>
      </button>

      <div className="chip-row" role="group" aria-label="Feed filters">
        <button
          type="button"
          className={`chip ${myPostsOnly ? "chip--active" : ""}`}
          aria-pressed={myPostsOnly}
          onClick={() => setMyPostsOnly((v) => !v)}
        >
          My posts
        </button>
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`chip ${sortBy === option.value ? "chip--active" : ""}`}
            aria-pressed={sortBy === option.value}
            onClick={() => setSortBy(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {feedLoading ? (
        <div className="feed-list">
          {[0, 1].map((i) => (
            <div key={i} className="app-card">
              <Skeleton avatar active paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      ) : feed.length === 0 ? (
        <div className="app-empty">
          {myPostsOnly ? "You haven't posted yet." : "No posts yet. Be the first one to post."}
        </div>
      ) : (
        <div className="feed-list">
          {feed.map((post) => (
            <FeedPostCard
              key={post._id}
              post={post}
              commentText={commentByPost[post._id] || ""}
              onCommentTextChange={(value) =>
                setCommentByPost((prev) => ({ ...prev, [post._id]: value }))
              }
              onCommentSubmit={() => handleAddComment(post._id)}
              onLikeToggle={() => handleToggleLike(post._id)}
              onShare={() => handleSharePost(post._id)}
              onPostEdit={(text, done) =>
                editPostAction(
                  { postId: post._id, text },
                  {
                    onSuccess: () => done?.(),
                  }
                )
              }
              onCommentEdit={(commentId, text, done) =>
                editCommentAction(
                  { postId: post._id, commentId, text },
                  {
                    onSuccess: () => done?.(),
                  }
                )
              }
              liking={liking}
              currentUserId={endUser?._id}
              commenting={commenting}
              editingPost={editingPost}
              editingComment={editingComment}
            />
          ))}
        </div>
      )}

      {hasNextPage && (
        <div className="feed-more">
          <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
            Load more posts
          </Button>
        </div>
      )}

      <Sheet open={composerOpen} onClose={() => setComposerOpen(false)}>
        <h2 className="sheet-form__title feed-sheet-title">Create post</h2>
        <FeedComposer
          postText={postText}
          setPostText={setPostText}
          postType={postType}
          setPostType={setPostType}
          onPost={handleCreatePost}
          posting={posting}
          currentUser={endUser}
        />
      </Sheet>
    </AppShell>
  );
};

export default Feed;
